// Autocomplete implementation for the top bar search

import type { LiteClient } from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { enableScrolling, disableScrolling, wait } from '../../utils';
import { stardust } from '@eidellev/adonis-stardust/client';
import { getIndexName } from '../../../../../app/Libraries/Algolia/AlgoliaUtils';
import { generateTransformedImageUrl, SMALL_PLACEHOLDER_IMAGE_URL } from '../../../../../app/Libraries/Utils';
import { closeAllSubMenus, closeMobileNav } from '../../nav';
import aa from '../algolia-insights';
import { enableQidLinks } from '../qid-link';

// import type { Render } from '@algolia/autocomplete-js';

let isSearchOpen = false;
let latestState: any = null;
let autocompleteInstance: any = null;

export default (searchClient: LiteClient, container: HTMLElement) => {
  enableQidLinks();

  let isRefreshing = false;

  let didLockScroll = false;

  const observeSearchPanel = () => {
    const observer = new MutationObserver((_mutations, obs) => {
      const searchPanel = document.querySelector<HTMLElement>('.aa-Panel');
      if (searchPanel && !didLockScroll) {
        disableScrolling();
        didLockScroll = true;
        obs.disconnect();

        requestAnimationFrame(() => updatePanelHeight(true));

        // Optional: Watch for panel removal to re-enable scroll
        const removalObserver = new MutationObserver(() => {
          const stillThere = document.body.contains(searchPanel);
          if (!stillThere && didLockScroll) {
            enableScrolling();
            didLockScroll = false;
            removalObserver.disconnect();
          }
        });

        removalObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  /** Check which container we're in, for styling */
  const isFixedOverlay = () => {
    // TODO: This seems to work except the mobile one is always the "isHeader" one?
    const isFixed = container.closest('.nav__menu.js-mobile-nav.fixed');
    // const isHeader = container.closest('.nav__main > .nav__search');

    // console.debug('Opening search for ', container, { isFixed, isHeader });

    return !!isFixed;
  };
  const openSearch = () => {
    if (isSearchOpen) {
      return;
    }

    closeMobileNav();
    closeAllSubMenus();

    // waitForPanel();
    disableScrolling();
    enableArrowKeyNavigation();

    // container.classList.add('search-open');
    // console.debug('Search opened');
  };

  const closeSearch = () => {
    if (!isSearchOpen) {
      return;
    }

    disableArrowKeyNavigation();
    enableScrolling();
    requestAnimationFrame(() => updatePanelHeight(false));
    isSearchOpen = false;
  };

  /** Trim slash characters at the boundaries (not inside the path) */
  const normalizeDepartmentPath = (path?: string) => {
    if (!path) {
      return '';
    }

    return path.replace(/^\/+|\/+$/g, '');
  };

  /**
   * Just grab first path from the item.
   * It doesn't matter which path we have for the autocomplete, because the products inside would be the same.
   */
  const getDepartmentPathFromItem = (item) => {
    if (item.objectID === 'all-departments') {
      return '';
    }

    if (Array.isArray(item.paths) && typeof item.paths[0] === 'string') {
      return normalizeDepartmentPath(item.paths[0]);
    }

    // Legacy support for single path, but we should be using the paths array now
    if (typeof item.path === 'string' && item.path) {
      return normalizeDepartmentPath(item.path);
    }

    return '';
  };

  /** Split a department path into its component slugs */
  const getDepartmentPathSegments = (path?: string) => {
    return normalizeDepartmentPath(path)
      .split('/')
      .filter(Boolean);
  };

  /** Build an Algolia filters string from the deepest available menu level on a department hit */
  const getDepartmentMenuFilterFromItem = (item): string | undefined => {
    if (item.objectID === 'all-departments' || !item.menu || typeof item.menu !== 'object') {
      return undefined;
    }

    let deepestLevel = -1;
    let deepestKey = '';
    let deepestValues: string[] = [];

    Object.entries(item.menu).forEach(([key, values]) => {
      if (!/^lvl\d+$/.test(key) || !Array.isArray(values) || values.length === 0) {
        return;
      }

      const level = Number(key.replace('lvl', ''));
      if (Number.isNaN(level) || level < deepestLevel) {
        return;
      }

      deepestLevel = level;
      deepestKey = key;
      deepestValues = values.filter((value): value is string => typeof value === 'string' && !!value);
    });

    if (!deepestKey || deepestValues.length === 0) {
      return undefined;
    }

    const clauses = deepestValues.map((value) => {
      const escapedValue = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `productGroup.menu.${deepestKey}:"${escapedValue}"`;
    });

    return clauses.length === 1
      ? clauses[0]
      : `(${clauses.join(' OR ')})`;
  };

  const getDepartmentUrl = (item, state) => {
    // console.debug('Getting department URL for item:', item);

    // If it's the "All Departments" item, go to the archive page
    if (item.objectID === 'all-departments') {
      return stardust.route('shop.archive', undefined, { qs: { q: state.query } });
    }

    const departmentPath = getDepartmentPathFromItem(item);
    const departmentPathSegments = getDepartmentPathSegments(departmentPath);

    const url = departmentPathSegments.length > 0
      ? stardust.route('shop.department', { '*': departmentPathSegments })
      : stardust.route('shop.archive');
    return url;
  };

  // Used by getItemUrl (keyboard navigation via the library's navigator), which
  // is a JS-only navigation, so the qid tracking param is safe to include here.
  const getProductUrl = (item) => {
    // If Algolia provides a hit.__autocomplete_queryID on the hit, include it as qid in the product URL
    const opts = item?.__autocomplete_queryID
      ? { qs: { qid: item.__autocomplete_queryID } }
      : undefined;

    return stardust.route('shop.product', { slug: item.slug }, opts);
  };

  // Used in the rendered anchor's href, which crawlers can follow, so it must
  // stay clean of tracking params; qid-link.ts appends qid at click time.
  const getCleanProductUrl = (item) => stardust.route('shop.product', { slug: item.slug });

  function getDepartmentHitsPerPage() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      return 7;
    } else {
      return 3;
    }
  }

  function getProductHitsPerPage() {
    if (window.matchMedia('(min-width: 1685px)').matches) {
      return 10;
    } else if (window.matchMedia('(min-width: 1455px)').matches) {
      return 8;
    } else if (window.matchMedia('(min-width: 1006px)').matches) {
      return 9;
    } else {
      return 6;
    }
  }

  autocompleteInstance = autocomplete({
    container,
    placeholder: 'What can we help you find?',
    // https://www.algolia.com/doc/libraries/search-insights
    insights: {
      insightsClient: aa,
    },
    // Keeps the panel open on blur. Handy for inspecting it in dev tools, but never leave it on in production.
    // debug: true,
    panelPlacement: 'full-width',
    enterKeyHint: 'search',
    detachedMediaQuery: 'none',
    defaultActiveItemId: 0,
    // TODO: It complains when I add getItemUrl(), though the docs tell me to...
    // https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/sources/#dynamic-sources
    // @ts-ignore
    getSources({ query, state, refresh }) {
      return [
        /***************************************
         * Departments
         ***************************************/
        {
          sourceId: 'departments',
          getItems() {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: getIndexName('product_groups'),
                  params: {
                    query,
                    hitsPerPage: getDepartmentHitsPerPage(),
                  },
                },
              ],
              transformResponse({ hits }) {
                // Add an "All Departments" hit result to the beginning all the time
                const hitsArray = hits[0];
                hitsArray.unshift({
                  name: 'All Departments',
                  slug: '',
                  objectID: 'all-departments',
                });
                return hits;
              },
            });
          },
          getItemUrl({ item, state }) {
            return getDepartmentUrl(item, state);
          },
          onActive({ item, setContext, setActiveItemId }) {
            // For some reason when you call refresh() it doesn't set the active item,
            // so we set it ourselves, but need to prevent a loop
            // TODO: There is probably a cleaner way to do this, but I don't have time to figure it out now
            if (isRefreshing) {
              return;
            }

            // console.debug('Selected department:', item);
            // TODO: the __ makes me think this is internal and shouldn't be used,
            // but for some reason state.activeItemId is always null...
            const itemIndex = item.__autocomplete_id as number;
            // const itemIndex = state.activeItemId;
            // console.debug('Active item index:', state.activeItemId);

            // Keep track of which department they have selected (hovered), so we can use that for the product search.
            const selectedDepartmentPath = item.name === 'All Departments'
              ? undefined
              : getDepartmentPathFromItem(item);
            const selectedDepartmentFilter = item.name === 'All Departments'
              ? undefined
              : getDepartmentMenuFilterFromItem(item);
            setContext({ selectedDepartmentPath, selectedDepartmentFilter });

            // Store the active item index
            // console.debug('Active item ID:', item.__autocomplete_id);

            // Trigger a re-search
            isRefreshing = true;
            refresh().then(() => {
              // Restore the active item after refresh
              setActiveItemId(itemIndex);
              isRefreshing = false;
            }).catch(() => {
              isRefreshing = false;
            });
          },
          templates: {
            header({ state, html }) {
              return html`
              <span class="aa-SourceHeaderTitle">
                Searching: ${state.query}
              </span>`;
            },
            item({ item, components, html }) {
              return html`
              <a class="aa-ItemLink" href="${getDepartmentUrl(item, state)}">
                <div class="aa-ItemContent">
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentTitle">
                      ${components.Highlight({ hit: item, attribute: 'name' })}
                    </div>
                  </div>
                </div>
              </a>`;
            },
            noResults() {
              return 'No results.';
            },
          },
        },
        /***************************************
         * Products
         ***************************************/
        {
          sourceId: 'products',
          getItems() {
            // console.debug('Getting products', state, state.context?.selectedDepartmentPath);

            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: getIndexName('products'),
                  params: {
                    // TODO: This should match the products index menu structure,
                    // e.g. productGroup.menu.lvl2:"A > B > C"
                    // Add search params from the state
                    filters: state.context?.selectedDepartmentFilter
                      ? state.context.selectedDepartmentFilter as string
                      : undefined,
                    query,
                    hitsPerPage: getProductHitsPerPage(),
                  },
                },
              ],
            });
          },
          getItemUrl({ item }) {
            return getProductUrl(item);
          },
          templates: {
            // NOTE: We're injecting fake header() template as a sibling in render() instead of using
            // the actual template so we have the body and footer grouped and scrollable
            // header({ html }) {
            //   return html`
            //   <span class="aa-SourceHeaderTitle">
            //     Top Products
            //   </span>`;
            // },
            item({ item, components, html }) {
              const imageUrl = item.imageUrl as string | undefined;
              const imageSection = imageUrl ?
                html`
                <picture>
                  <source srcset="${generateTransformedImageUrl(imageUrl, { format: 'webp', fit: 'scale-down', width: 120 })}" type="image/webp" />
                  <source srcset="${generateTransformedImageUrl(imageUrl, { format: 'jpeg', fit: 'scale-down', width: 120 })}" type="image/jpeg" />
                  <img src="${generateTransformedImageUrl(imageUrl, { format: 'jpeg', fit: 'scale-down', width: 120 })}" alt="" />
                </picture>` :
                html`
                <picture>
                  <source srcset="${generateTransformedImageUrl(SMALL_PLACEHOLDER_IMAGE_URL, { format: 'webp', fit: 'scale-down', width: 120 })}" type="image/webp" />
                  <source srcset="${generateTransformedImageUrl(SMALL_PLACEHOLDER_IMAGE_URL, { format: 'jpeg', fit: 'scale-down', width: 120 })}" type="image/jpeg" />
                  <img src="${generateTransformedImageUrl(SMALL_PLACEHOLDER_IMAGE_URL, { format: 'jpeg', fit: 'scale-down', width: 120 })}" alt="No product image available" />
                </picture>`;

              return html`
              <a class="aa-ItemLink" href="${getCleanProductUrl(item)}" data-qid="${item.__autocomplete_queryID ?? ''}">
                <div class="aa-ItemContent">
                  <div class="aa-ItemIcon">
                    ${imageSection}
                  </div>
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentDescription aa-ItemContentDescription--bold">
                      ${ // TODO: nested not working?
                        // https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#component-attribute-2
                        // components.Snippet({ hit: item, attribute: 'brand[name]' })
                        item.brand ? (item.brand as any).name : ''
                      }
                    </div>
                    <div class="aa-ItemContentTitle">
                      ${components.Highlight({ hit: item, attribute: 'name' })}
                    </div>
                    <div class="aa-ItemContentDescription">
                      SKU #${components.Snippet({ hit: item, attribute: 'sku' })}
                    </div>
                    ${item.restricted === true ? html`<div class="aa-ItemTag"><span class="product-tag">Restricted product</span></div>` : ''}
                  </div>
                  <div class="aa-ItemActions">
                    <button
                      class="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly"
                      type="button"
                      title="Select"
                    >
                      <svg width="18.211" height="14.688" viewBox="0 0 18.211 14.688" fill="currentColor">
                        <path d="m9.431 13.268 4.975-4.947H0v-2h14.338l-4.906-4.9L10.844.003l7.367 7.361-7.371 7.326Z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </a>`;
            },
            noResults() {
              return 'No results.';
            },
            footer({ state, html }) {
              // console.debug('Rendering footer with state:', state);
              const productResults = state.collections[1].items;
              const hasResults = productResults.length > 0;
              if (!hasResults) {
                return html``;
              }

              const selectedDepartmentPath = state.context?.selectedDepartmentPath as string | undefined;
              const departmentPathSegments = getDepartmentPathSegments(selectedDepartmentPath);

              const url = departmentPathSegments.length > 0
                ? stardust.route('shop.department', { '*': departmentPathSegments }, { qs: { q: state.query } })
                : stardust.route('shop.archive', undefined, { qs: { q: state.query } });

              return html`
              <div class="aa-ItemWrapper">
                <div class="aa-ItemContent">
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentTitle">
                      <a class="btn btn--primary" href="${url}">
                        <span>
                          View all results
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18.211" height="14.688" viewBox="0 0 18.211 14.688" class="icon icon--fill icon--margin-left">
                          <path d="m9.431 13.268 4.975-4.947H0v-2h14.338l-4.906-4.9L10.844.003l7.367 7.361-7.371 7.326Z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>`;
            },
          },
        },
      ];
    },
    onStateChange({ state, setContext }) {
      // console.debug('State changed:', state);
      latestState = state;

      // If not open, reset the department selection
      if (!state.isOpen && state.context?.selectedDepartmentPath) {
        // console.debug('Not open. Resetting department selection');
        setContext({ selectedDepartmentPath: undefined, selectedDepartmentFilter: undefined });
      }

      if (state.isOpen && !isSearchOpen) {
        openSearch();
        observeSearchPanel(); // observe for real panel render
        isSearchOpen = true;
      } else if (!state.isOpen && isSearchOpen) {
        closeSearch();
        isSearchOpen = false;
      }
    },
    // NOTE: This doesn't actually fire because it uses onSelect() for the currently selected result,
    // which is the "all departments" one by default.
    // onSubmit({ state }) {
    //   console.debug('Form submitted with query:', state.query);

    //   // const filters = search.helper?.state.filters;
    //   // const isWanted = filters?.includes('isWanted:true');
    //   const qs = {
    //     q: state.query,
    //   };

    //   // Navigate to results page
    //   const submitUrl = stardust.route('shop.archive', undefined, { qs });
    //   window.location.href = submitUrl;
    //   return;
    // },
    render({ elements, render, html }, root) {
      const { departments, products } = elements;

      // TODO: How do I attach event listeners like I have for open and close in the instantsearch version?

      render(
        // Injecting fake header() template here as a sibling so we have the body and footer grouped and scrollable

        // TODO: It complains about some types, but this is from the docs and works...
        // https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#param-render
        // @ts-ignore
        html`
        <div class="aa-PanelLayout ${isFixedOverlay() ? 'aa-PanelLayout--isFixed' : ''}">
          <div class="search__results search__results--departments">${departments}</div>
          <div class="search__results search__results--products">
            <div class="aa-SourceHeader">
              <span class="aa-SourceHeaderTitle">
                Top Products
              </span>
            </div>
            ${products}
          </div>
        </div>`,
        root,
      );
    },
  });

  let lastIsMobile = window.innerWidth < 768;

  window.addEventListener('resize', () => {
    const isNowMobile = window.innerWidth < 768;

    if (isNowMobile !== lastIsMobile) {
      lastIsMobile = isNowMobile;

      if (latestState?.isOpen) {
        autocompleteInstance.setIsOpen(false);
        autocompleteInstance.setContext({ selectedDepartmentPath: undefined, selectedDepartmentFilter: undefined });
        closeSearch();
      }
    }
  });

  document.addEventListener('nav:open', async () => {
    if (!latestState?.isOpen) {
      return;
    }

    const input = container.querySelector<HTMLInputElement>('.aa-Input');

    if (input) {
      // slight timeout to let nav animation happen
      if (!window.matchMedia('(min-width: 1080px)').matches) {
        await wait(600);
      }

      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    autocompleteInstance.setContext({ selectedDepartmentPath: undefined, selectedDepartmentFilter: undefined });
    closeSearch();
  });

  function enableArrowKeyNavigation() {
    document.addEventListener('keydown', handleAutocompleteKeydown);
  }
};

function handleAutocompleteKeydown(e: KeyboardEvent) {
  const input = document.activeElement as HTMLInputElement;
  const activeId = input?.getAttribute('aria-activedescendant');

  if (!activeId || !latestState?.collections) {
    return;
  }

  const activeEl = document.getElementById(activeId);
  if (!activeEl) {
    return;
  }

  const allItems: { item: any; sourceId: string; index: number }[] = [];
  latestState.collections.forEach((collection) => {
    collection.items.forEach((item) => {
      allItems.push({
        item,
        sourceId: collection.source.sourceId,
        index: allItems.length,
      });
    });
  });

  const activeItemIndex = allItems.findIndex(
    (entry) => entry.item.__autocomplete_id === latestState.activeItemId
  );

  if (e.key === 'ArrowRight') {
    const current = allItems[activeItemIndex];
    if (current?.sourceId === 'departments') {
      const target = allItems.find((entry) => entry.sourceId === 'products');
      if (target) {
        e.preventDefault();
        autocompleteInstance.setActiveItemId(target.index);
      }
    }
  }

  if (e.key === 'ArrowLeft') {
    const current = allItems[activeItemIndex];
    if (current?.sourceId === 'products') {
      const target = allItems.find((entry) => entry.sourceId === 'departments');
      if (target) {
        e.preventDefault();
        autocompleteInstance.setActiveItemId(target.index);
      }
    }
  }
}

function disableArrowKeyNavigation() {
  document.removeEventListener('keydown', handleAutocompleteKeydown);
}

function updatePanelHeight(isOpen: boolean) {
  const panel = document.querySelector('.aa-Panel') as HTMLElement;
  if (panel) {
    if (!isOpen) {
      panel.style.height = '';
      return;
    }

    const rect = panel.getBoundingClientRect(); // Get position relative to viewport
    panel.style.height = `calc(100dvh - ${rect.top}px`;
  }
}
